import { randomId } from "../random";
import type { ExcalidrawElement } from "./types";
import type { Mutable } from "../utility-types";

/**
 * Re-mint a duplicated mindmap's CANVAS identity.
 *
 * `customData` rides through `deepCopyElement` verbatim, so a duplicated map
 * used to keep the original's `mapId`. Every consumer indexes nodes by
 * `mapId::blockId` with last-wins semantics, so the twin silently captured the
 * original's connectors (cross-wired arrows), hid one copy from the refresh
 * diff, and emptied the expand chip's child list. A copy is a NEW map on the
 * canvas and needs its own `mapId`.
 *
 * `blockId`, `parentBlockId` and `rootPageId` are NOTION identity, not canvas
 * identity: both copies still describe the same Notion content, and refresh /
 * expand need them to find their source. They are preserved.
 *
 * PARTIAL SELECTION RULE — a map is re-minted only when the duplicated set
 * contains that map's ROOT node. Duplicating a whole map yields an independent
 * second map; duplicating a handful of pills out of the middle yields more
 * pills OF THE SAME map, which is the only thing the rest of the pipeline can
 * represent: a rootless map has no deletion ledger (it lives on the root), no
 * refresh anchor and no reflow origin, so minting one would create a map that
 * can never be refreshed, expanded or collapsed. Keeping the original `mapId`
 * leaves the copies as ordinary (duplicate-blockId) members of the map they
 * came from, which the defensive index in `mindmapBinding` degrades safely.
 */

/** The subset of `customData.mindmap` this module reads or rewrites. */
interface MindmapIdentityData {
  mapId: string;
  blockId: string;
  role: "node" | "connector";
  rootPageId: string;
  parentBlockId?: string;
  remembered?: { elements?: ExcalidrawElement[] };
}

export interface MindmapRemintPlan {
  /** Old → new `mapId`, populated only for maps whose root is being copied. */
  readonly mapIds: ReadonlyMap<string, string>;
}

const mindmapOf = (
  element: ExcalidrawElement,
): MindmapIdentityData | undefined => {
  const mm = element.customData?.mindmap;
  return mm && typeof mm.mapId === "string"
    ? (mm as MindmapIdentityData)
    : undefined;
};

/**
 * A map's root is the node whose blockId IS the Notion source id, or — for
 * legacy maps emitted before `parentBlockId` existed on every pill — the only
 * node with no parent. Same structural test the app-side ledger uses.
 */
const isRootNode = (mm: MindmapIdentityData): boolean =>
  mm.role === "node" && (mm.blockId === mm.rootPageId || !mm.parentBlockId);

/** Decide which of the maps present in `elements` earn a fresh `mapId`. */
export const planMindmapRemint = (
  elements: readonly ExcalidrawElement[],
): MindmapRemintPlan => {
  const mapIds = new Map<string, string>();
  for (const element of elements) {
    const mm = mindmapOf(element);
    if (mm && isRootNode(mm) && !mapIds.has(mm.mapId)) {
      mapIds.set(mm.mapId, randomId());
    }
  }
  return { mapIds };
};

/**
 * Give every element of a remembered (collapsed) subtree a fresh id.
 *
 * `remembered.elements` are stored VERBATIM, ids included, so a duplicated map
 * carries a second copy of ids that are not currently in the scene — but would
 * be the moment either copy is re-expanded, inserting two elements that share
 * an id. References are rewritten within the subtree; a reference that resolves
 * to nothing in it pointed at a LIVE element of the original map, so it is
 * dropped rather than left to cross-wire (the binding pass rebuilds it from
 * `mapId`/`blockId` on the next expand).
 */
const remintRememberedElements = (
  elements: ExcalidrawElement[],
  newMapId: string | undefined,
): ExcalidrawElement[] => {
  const newIds = new Map<string, string>();
  for (const element of elements) {
    newIds.set(element.id, randomId());
  }

  return elements.map((element) => {
    const next = element as Mutable<ExcalidrawElement>;
    next.id = newIds.get(element.id)!;

    if (next.boundElements) {
      next.boundElements = next.boundElements.flatMap((bound) => {
        const id = newIds.get(bound.id);
        return id ? [{ ...bound, id }] : [];
      });
    }
    for (const key of ["containerId", "frameId"] as const) {
      const ref = (next as Record<string, unknown>)[key];
      if (typeof ref === "string") {
        (next as Record<string, unknown>)[key] = newIds.get(ref) ?? null;
      }
    }
    for (const key of ["startBinding", "endBinding"] as const) {
      const binding = (next as Record<string, unknown>)[key] as
        | { elementId: string }
        | null
        | undefined;
      if (binding) {
        const id = newIds.get(binding.elementId);
        (next as Record<string, unknown>)[key] = id
          ? { ...binding, elementId: id }
          : null;
      }
    }

    // A remembered subtree can itself contain a collapsed node.
    remintMindmapCustomData(next, newMapId);
    return next;
  });
};

/** Rewrite one already-copied element's mindmap identity in place. */
const remintMindmapCustomData = (
  element: Mutable<ExcalidrawElement>,
  newMapId: string | undefined,
): void => {
  const mm = mindmapOf(element);
  if (!mm) {
    return;
  }
  const next: MindmapIdentityData = { ...mm };
  if (newMapId) {
    next.mapId = newMapId;
  }
  if (next.remembered?.elements?.length) {
    next.remembered = {
      ...next.remembered,
      elements: remintRememberedElements(
        next.remembered.elements,
        newMapId ?? next.mapId,
      ),
    };
  }
  element.customData = { ...element.customData, mindmap: next };
};

/**
 * Apply a plan to one freshly-made copy. Mutates — the copy is a private deep
 * clone that has not reached the scene yet, which is how the surrounding
 * duplication code treats it.
 */
export const remintMindmapClone = (
  plan: MindmapRemintPlan,
  copy: Mutable<ExcalidrawElement>,
): void => {
  const mm = mindmapOf(copy);
  if (!mm) {
    return;
  }
  remintMindmapCustomData(copy, plan.mapIds.get(mm.mapId));
};

/** Plan-and-apply for the paths that clone a whole element array at once. */
export const remintDuplicatedMindmaps = (
  copies: readonly Mutable<ExcalidrawElement>[],
): void => {
  const plan = planMindmapRemint(copies);
  for (const copy of copies) {
    remintMindmapClone(plan, copy);
  }
};
